import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Box, Typography } from '@mui/material';
import { useViewport } from '../../../../hooks';

export const InfoRowsViewer: FC<Props> = ({ rows }: Props) => {
  const navigate = useNavigate();
  const isMobile = useViewport('sm');

  const handleRowClick = (link: string): void => {
    navigate(link);
  };

  const handleListItemClick = (item: string, onClick?: (item: string) => void): void => {
    if (onClick) {
      onClick(item);
    }
  };

  const renderValue = (row: InfoRow, isMobile: boolean): JSX.Element => {
    switch (row.type) {
      case 'link':
        return (
          <span
            className="cursor-pointer text-blue-500 underline hover:text-blue-700"
            onClick={(): void => handleRowClick(row.link)}
          >
            <Typography variant={isMobile ? 'body2' : 'body2'}>{row.value ?? '--'}</Typography>
          </span>
        );

      case 'list':
        if (row.items.length === 0) {
          return <span>--</span>;
        }
        return (
          <Box className="flex flex-wrap gap-1">
            {row.items.map((item: string, index: number) => (
              <Chip
                key={index}
                label={item}
                size="small"
                variant="outlined"
                className="cursor-pointer text-xs hover:bg-gray-100"
                onClick={(): void => handleListItemClick(item, row.onItemClick)}
              />
            ))}
          </Box>
        );

      case 'default':
        return <Typography variant="body2">{row.value ?? '--'}</Typography>;
    }
  };

  return (
    <div className="grid flex-grow auto-rows-min grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-sm">
      {rows !== null &&
        rows !== undefined &&
        rows.map((row: InfoRow, index: number) => (
          <React.Fragment key={index}>
            <Typography variant={isMobile ? 'body2' : 'body2'} className="font-semibold text-right">
              {row.title}
            </Typography>
            <div className="flex min-w-0 items-center gap-2">
              {renderValue(row, isMobile)}
              {row.badge !== null && row.badge !== undefined && (
                <Chip
                  label={row.badge}
                  size="small"
                  variant="filled"
                  className="bg-gray-100 text-xs text-gray-600"
                />
              )}
            </div>
          </React.Fragment>
        ))}
    </div>
  );
};

interface BaseInfoRow {
  title: string;
  badge?: string | null;
}

interface DefaultInfoRow extends BaseInfoRow {
  type: 'default';
  value: string | null;
}

interface LinkInfoRow extends BaseInfoRow {
  type: 'link';
  value: string | null;
  link: string;
}

interface ListInfoRow extends BaseInfoRow {
  type: 'list';
  items: string[];
  onItemClick?: (item: string) => void;
}

export type InfoRow = DefaultInfoRow | LinkInfoRow | ListInfoRow;

interface Props {
  rows?: InfoRow[] | null;
}
